import {
  getSafeUrl,
  useAppSelector,
  useAppDispatch,
  getLocalSelectedTipTab,
  updateLocalSelectedTipTab
} from '@monorepo/shared';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonToolbar,
  FlexboxGrid,
  IconButton,
  List,
  Loader,
  Stack,
  Tabs,
  useToaster
} from 'rsuite';
import { useEffect, useState } from 'react';

import {
  selectHaEditMasterOnly,
  selectRegions
} from '@monorepo/shared/store/authSlice';
import {
  selectAccountAllHa,
  selectAccountAllInstances,
  selectAccountHaCreatable
} from '@monorepo/shared/store/haInstanceSlice';
import DetailIcon from '@rsuite/icons/Detail';
import { IoEnterOutline } from 'react-icons/io5';
import PlusIcon from '@rsuite/icons/Plus';
import ReloadIcon from '@rsuite/icons/Reload';
import { Accordion } from 'rsuite';
import {
  columnLeftWithHeight,
  dataStyle,
  slimText,
  styleCenterWithHeight
} from './AccountDetail';
import {
  regionHaInstanceApi,
  useGetRegionsInstanceListQuery,
  useLazyGetHaInstanceAccessUrlQuery
} from '@monorepo/shared/services/regionHaInstance';
import { ErrorToaster } from './InstanceStatus';
import {
  regionHaApi,
  useGetRegionsHaListQuery
} from '@monorepo/shared/services/regionHa';
import { CustomTooltip } from '../../components/CustomTooltip';
import { ClusterHelper } from '../../config/const';

import CheckRoundIcon from '@rsuite/icons/CheckRound';
import MinusRoundIcon from '@rsuite/icons/MinusRound';
import { InstanceItemRender } from './InstanceItem';
import { selectHaGroups } from '@monorepo/shared/store/groupSlice';
import { TrialActive } from './TrialActive';

export const InstanceList = () => {
  const { groupId } = useParams();
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const navigate = useNavigate();
  const [items, setItems] = useState<Record<string, any>[]>([]);
  const [haItems, setHaItems] = useState<Record<string, any>[]>([]);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const haEditMasterOnly = useAppSelector(selectHaEditMasterOnly);
  const regions = useAppSelector(selectRegions);
  const haGroups = useAppSelector(selectHaGroups);
  const accountRegionInstances = useAppSelector(selectAccountAllInstances);
  const accountRegionHas = useAppSelector(selectAccountAllHa);
  const accountHaCreatable = useAppSelector(selectAccountHaCreatable);
  const [haCreatable, setHaCreatable] = useState<Record<string, any>>({});
  const [enableCreateHa, setEnableCreateHa] = useState<boolean>(false); // tip default disable create HA
  const [disableEnter, setDisableEnter] = useState<boolean>(false);
  const [queryRegions, setQueryRegions] = useState<Record<string, any>[]>([]);
  const localSelectedTipTab = getLocalSelectedTipTab();
  const [activeKey, setActiveKey] = useState<string | undefined>(
    localSelectedTipTab || 'instance'
  );
  const [
    getInstanceUrl,
    {
      data: urlData,
      isLoading: urlIsLoading,
      isFetching: urlIsFetching,
      error: urlError
    }
  ] = useLazyGetHaInstanceAccessUrlQuery();
  const queryParams = {
    account_id: currentAccount.account_id,
    user_id: currentAccount.user_id,
    access_from_user_id:
      currentAccount.access_from_user_id || currentAccount.access_from?.user_id,
    group_id: groupId,
    regions: queryRegions,
    account_region: currentAccount.region
  };
  const {
    data: regionsData,
    isLoading,
    isFetching
  } = useGetRegionsInstanceListQuery(
    {
      ...queryParams
    },
    { skip: !queryRegions.length }
  );
  const { data: haData } = useGetRegionsHaListQuery(
    {
      ...queryParams
    },
    { skip: !queryRegions.length }
  );

  const reload = () => {
    dispatch(regionHaApi.util.invalidateTags(['HA']));
    dispatch(regionHaInstanceApi.util.invalidateTags(['RegionInstance']));
    dispatch(regionHaInstanceApi.util.invalidateTags(['VmStatus']));
  };
  const goToHaCreate = () => {
    navigate(`ha/create`);
  };
  const goToHADetail = (id: string) => {
    navigate(`ha/${id}`);
  };
  useEffect(() => {
    const data = accountHaCreatable?.[currentAccount.account_id];
    if (data) {
      setHaCreatable(data);
    }
  }, [accountHaCreatable, currentAccount]);
  useEffect(() => {
    const accountData = accountRegionInstances?.[currentAccount?.account_id];
    if (accountData) {
      const data = accountData.filter(
        (obj: Record<string, any>) => !obj?.ha_id
      );
      setItems(data);
    }
  }, [accountRegionInstances, currentAccount]);
  useEffect(() => {
    const accountData = accountRegionHas?.[currentAccount?.account_id];
    if (accountData) {
      const hData = accountData;
      setHaItems(hData);
      if (!localSelectedTipTab && accountData?.length && enableCreateHa) {
        setActiveKey('cluster');
      }
    }
  }, [accountRegionHas, currentAccount]);
  useEffect(() => {
    if (groupId) {
      setEnableCreateHa(false);
      if (
        haGroups?.[groupId] &&
        haGroups?.[groupId].permission === 'ssh_only'
      ) {
        setDisableEnter(true);
      }
    } else if (haEditMasterOnly && currentAccount?.user_type !== 'Master') {
      setEnableCreateHa(false);
    } else if (
      !currentAccount?.has_instance &&
      !currentAccount?.region?.length
    ) {
      setEnableCreateHa(false);
    }
  }, [groupId, haEditMasterOnly, currentAccount, haGroups]);
  useEffect(() => {
    if (urlData?.url) {
      window.open(getSafeUrl(urlData.url), '_blank');
    }
    if (urlError) {
      ErrorToaster({
        toaster,
        error: urlError,
        title: 'Fetch instance login URL failed!'
      });
    }
  }, [urlData, urlError]);

  // need to fetch instance list at first time event no region in account
  // so can trigger the invalidateTags when first provision
  useEffect(() => {
    const regionArr = Object.values(regions)?.filter(
      (obj: Record<string, any>) => obj?.token
    );
    if (regionArr?.length) {
      setQueryRegions(regionArr);
    }
  }, [regions]);
  return (
    <div className="grid-item instance-list big">
      <div className="grid-header">
        <div className="title">
          <i className="icon-fortinetone-productViewIcon"></i>
          {/* Instances / Clusters */}
          {`Instances ${enableCreateHa ? '/ Clusters' : ''}`}
        </div>
        <Stack spacing={20}>
          {enableCreateHa && (
            <IconButton
              title="Add New Cluster"
              icon={<PlusIcon />}
              appearance="default"
              circle
              className="icon-btn"
              onClick={goToHaCreate}
            />
          )}
          <IconButton
            title="Reload"
            icon={<ReloadIcon />}
            appearance="default"
            circle
            className="icon-btn"
            onClick={reload}
          />
        </Stack>
      </div>
      <div className="grid-content custom-overflow">
        <div
          className="instance-list-content"
          style={{ color: 'var(--rs-text-heading)', height: '100%' }}
        >
          {isLoading ||
          isFetching ||
          !Object.values(regions)?.some(
            (obj: Record<string, any>) => obj?.token
          ) ? (
            <div style={{ height: '100%', position: 'relative' }}>
              <Loader size="xs" content="Loading..." center />
            </div>
          ) : (
            <>
              <Tabs
                activeKey={activeKey}
                onSelect={(tab: any) => {
                  if (tab) {
                    setActiveKey(tab);
                    updateLocalSelectedTipTab(tab);
                  }
                }}
                appearance="subtle"
              >
                <Tabs.Tab
                  eventKey="instance"
                  title="Standalone Instances"
                  key="instance"
                >
                  {items.length ? (
                    <List size="sm" style={{ boxShadow: 'unset' }}>
                      {items.map((item: Record<string, any>, index: number) => (
                        <List.Item key={index} index={index + 1}>
                          <InstanceItemRender
                            account={currentAccount}
                            item={item}
                          />
                          {item?.is_trial &&
                            (item?.need_resize ||
                              item?.resize_status === 'RESIZE_ERROR') && (
                              <TrialActive instance={item} />
                            )}
                        </List.Item>
                      ))}
                    </List>
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        placeItems: 'center',
                        height: '100%'
                      }}
                    >
                      <span>{'No instances have been provisioned'}</span>
                    </div>
                  )}
                </Tabs.Tab>
                {enableCreateHa && (
                  <Tabs.Tab eventKey="cluster" title="Clusters" key="cluster">
                    {haItems.length ? (
                      <Accordion>
                        {haItems.map(
                          (obj: Record<string, any>, index: number) => (
                            <Accordion.Panel
                              key={index + 1}
                              // caretAs={FaAngleDoubleDown}
                              header={
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  <FlexboxGrid>
                                    <FlexboxGrid.Item
                                      colspan={6}
                                      style={{
                                        ...columnLeftWithHeight,
                                        justifyContent: 'center'
                                      }}
                                    >
                                      <div>
                                        <div
                                          style={{
                                            ...slimText,
                                            color: 'var(--rs-primary-600)'
                                          }}
                                        >
                                          Cluster
                                        </div>
                                        <div
                                          style={dataStyle}
                                        >{`${obj?.ha_name} `}</div>
                                      </div>
                                    </FlexboxGrid.Item>
                                    <FlexboxGrid.Item
                                      colspan={2}
                                      style={styleCenterWithHeight}
                                    >
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={slimText}>Region</div>
                                        <div
                                          style={dataStyle}
                                        >{`${obj?.region_alias}`}</div>
                                      </div>
                                    </FlexboxGrid.Item>
                                    <FlexboxGrid.Item
                                      colspan={4}
                                      style={styleCenterWithHeight}
                                    >
                                      <div style={{ textAlign: 'right' }}>
                                        <div style={slimText}>Status</div>
                                        <div style={dataStyle}>
                                          {obj?.ha_status_text || 'Unknown'}
                                          <CustomTooltip
                                            content={
                                              ClusterHelper[obj?.ha_status]
                                            }
                                            trigger="hover"
                                          >
                                            {obj?.ha_status === 'IDLE' ? (
                                              <CheckRoundIcon
                                                style={{
                                                  fontSize: '12px',
                                                  marginLeft: '4px',
                                                  verticalAlign: 'middle',
                                                  color: 'green'
                                                }}
                                              />
                                            ) : (
                                              <MinusRoundIcon
                                                style={{
                                                  fontSize: '12px',
                                                  marginLeft: '4px',
                                                  verticalAlign: 'middle',
                                                  color: 'grey'
                                                }}
                                              />
                                            )}
                                          </CustomTooltip>
                                        </div>
                                      </div>
                                    </FlexboxGrid.Item>
                                    {obj?.ha_health ? (
                                      <FlexboxGrid.Item
                                        colspan={2}
                                        style={styleCenterWithHeight}
                                      >
                                        <div style={{ textAlign: 'center' }}>
                                          <div style={slimText}>Health</div>
                                          <div style={dataStyle}>
                                            {obj?.ha_health}
                                          </div>
                                        </div>
                                      </FlexboxGrid.Item>
                                    ) : (
                                      <FlexboxGrid.Item
                                        colspan={2}
                                        style={styleCenterWithHeight}
                                      ></FlexboxGrid.Item>
                                    )}
                                    <FlexboxGrid.Item
                                      colspan={2}
                                      style={styleCenterWithHeight}
                                    ></FlexboxGrid.Item>
                                    <FlexboxGrid.Item
                                      colspan={8}
                                      style={{
                                        ...styleCenterWithHeight,
                                        justifyContent: 'flex-end'
                                      }}
                                    >
                                      <ButtonToolbar>
                                        <Button
                                          startIcon={<DetailIcon />}
                                          appearance="ghost"
                                          size="xs"
                                          onClick={() =>
                                            goToHADetail(obj?.ha_id)
                                          }
                                        >
                                          Manage Cluster
                                        </Button>
                                        {obj?.ha_status === 'IDLE' &&
                                          obj.instances && (
                                            <Button
                                              loading={isLoading || isFetching}
                                              startIcon={<IoEnterOutline />}
                                              disabled={disableEnter}
                                              appearance="primary"
                                              size="xs"
                                              onClick={() => {
                                                getInstanceUrl({
                                                  ha_id: obj?.ha_id,
                                                  region: obj.regionObj,
                                                  account: currentAccount
                                                });
                                              }}
                                            >
                                              Login
                                            </Button>
                                          )}
                                      </ButtonToolbar>
                                    </FlexboxGrid.Item>
                                  </FlexboxGrid>
                                </div>
                              }
                              // defaultExpanded
                            >
                              {obj.instances ? (
                                <List size="sm">
                                  {obj.instances?.map(
                                    (
                                      item: Record<string, any>,
                                      index: number
                                    ) => (
                                      <List.Item key={index} index={index + 1}>
                                        <InstanceItemRender
                                          isCluster={true}
                                          account={currentAccount}
                                          item={item}
                                        />
                                      </List.Item>
                                    )
                                  )}
                                </List>
                              ) : (
                                <div
                                  style={{
                                    height: '50px',
                                    display: 'grid',
                                    placeItems: 'center'
                                  }}
                                >
                                  No Instance Members
                                </div>
                              )}
                            </Accordion.Panel>
                          )
                        )}
                      </Accordion>
                    ) : (
                      <div
                        style={{
                          display: 'grid',
                          placeItems: 'center',
                          height: '100%'
                        }}
                      >
                        <span>{'No cluster to show'}</span>
                      </div>
                    )}
                  </Tabs.Tab>
                )}
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
